import React, { useState, useEffect } from 'react';
import { Amplify } from 'aws-amplify';
import { Authenticator } from '@aws-amplify/ui-react';
import { getCurrentUser } from 'aws-amplify/auth';
import '@aws-amplify/ui-react/styles.css';
import awsconfig from '../aws-exports';
import { Landing } from '../pages/Landing';
import './Auth.css';

Amplify.configure(awsconfig);

interface AuthProps {
  children: React.ReactNode;
}

export const Auth: React.FC<AuthProps> = ({ children }) => {
  const [showAuth, setShowAuth] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [oauthError, setOauthError] = useState<string | null>(null);

  useEffect(() => {
    // Check for OAuth errors in URL
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    const errorDescription = params.get('error_description');

    if (error) {
      let friendlyMessage = 'Authentication failed. ';

      if (errorDescription) {
        const decoded = decodeURIComponent(errorDescription);
        if (decoded.includes('Attribute cannot be updated')) {
          friendlyMessage += 'Your account settings conflict with Google sign-in. Please contact support or try signing in with email/password.';
        } else {
          friendlyMessage += decoded;
        }
      } else {
        friendlyMessage += error;
      }

      setOauthError(friendlyMessage);

      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Check if user is already authenticated
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      await getCurrentUser();
      setIsAuthenticated(true);
    } catch (err) {
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while checking authentication
  if (isLoading) {
    return <div>Loading...</div>;
  }

  // Show OAuth error if present
  if (oauthError && !isAuthenticated) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: '20px'
      }}>
        <div style={{
          maxWidth: '500px',
          padding: '30px',
          background: '#fff',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          border: '2px solid #ef4444'
        }}>
          <h2 style={{ color: '#ef4444', marginTop: 0 }}>Sign-In Error</h2>
          <p style={{ color: '#374151', lineHeight: '1.6' }}>{oauthError}</p>
          <button
            onClick={() => {
              setOauthError(null);
              setShowAuth(false);
            }}
            style={{
              marginTop: '20px',
              padding: '12px 24px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '500'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // If authenticated, wrap in Authenticator so hooks work (Layout uses useAuthenticator)
  if (isAuthenticated) {
    return (
      <Authenticator
        socialProviders={['google']}
        loginMechanisms={['email']}
      >
        {({ user, signOut }) => (
          user ? <div className="authenticated-app">{children}</div> : <></>
        )}
      </Authenticator>
    );
  }

  // If user wants to authenticate, show Authenticator
  if (showAuth) {
    return (
      <div>
        {/* Back button to return to landing page */}
        <button
          onClick={() => setShowAuth(false)}
          style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            zIndex: 1000,
            padding: '10px 20px',
            background: '#f3f4f6',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          ← Back to Home
        </button>

        <Authenticator
          socialProviders={['google']}
          loginMechanisms={['email']}
          formFields={{
            signUp: {
              name: {
                label: 'Full Name',
                placeholder: 'Enter your full name',
                isRequired: true,
                order: 1
              },
              email: {
                label: 'Email',
                placeholder: 'Enter your email',
                isRequired: true,
                order: 2
              },
              password: {
                label: 'Password',
                placeholder: 'Enter your password',
                isRequired: true,
                order: 3
              },
              confirm_password: {
                label: 'Confirm Password',
                placeholder: 'Confirm your password',
                isRequired: true,
                order: 4
              }
            }
          }}
        >
          {({ user }) => {
            if (user) {
              // User just authenticated, update state
              setIsAuthenticated(true);
              return <div className="authenticated-app">{children}</div>;
            }
            return <></>;
          }}
        </Authenticator>
      </div>
    );
  }

  // Default: show landing page
  return (
    <Landing
      onSignIn={() => setShowAuth(true)}
      onSignUp={() => setShowAuth(true)}
    />
  );
};

export default Auth;
